/**
 * api/wallet.ts — typed wrappers for the read-only endpoints the web
 * portal needs. Matches api-contract.ts on the mobile side. Money is
 * always minor units as a string over the wire; we convert to bigint
 * at the boundary so components consume the correct type end-to-end.
 */

import { api } from "./client";

// ---------------------------------------------------------------------------
// Wire types — mirror the backend responses.
// ---------------------------------------------------------------------------

export interface MoneyWire {
  amount: string;
  asset: string;
}

export interface BalanceOut {
  available: MoneyWire;
  sendable: MoneyWire;
  pending: MoneyWire;
  held: MoneyWire;
  outbound_frozen_until?: string;
  external_withdrawal_frozen_until?: string;
}

export interface ActivityCounterparty {
  id: string;
  handle?: string | null;
  display_name?: string | null;
  avatar_url?: string | null;
  known?: boolean;
}

export interface ActivityItem {
  id: string;
  kind: string; // 'transfer' | 'topup' | 'qr_pay' | ...
  amount: MoneyWire;      // SIGNED — negative for outgoing
  direction: "in" | "out";
  status: "pending" | "completed" | "failed" | "reversed";
  title: string;
  subtitle: string;
  avatar_url?: string | null;
  initials?: string | null;
  occurred_at: string;
  resource: { kind: string; id: string };
}

export interface ActivityPage {
  data: ActivityItem[];
  next_cursor: string | null;
}

export interface MeOut {
  id: string;
  display_name: string | null;
  handle: string;
  avatar_url: string | null;
  phone_verified: boolean;
  email_verified: boolean;
  identity: {
    tier: number;
    status: string;
    limits: {
      balance_cap: MoneyWire;
      monthly_volume: MoneyWire;
      per_transaction: MoneyWire;
    };
    next_tier_requirements: string[];
  };
  created_at: string;
}

// ---------------------------------------------------------------------------
// Callers.
// ---------------------------------------------------------------------------

export function fetchBalance(): Promise<BalanceOut> {
  return api<BalanceOut>("/v1/balance");
}

export function fetchActivity(
  cursor?: string | null,
  limit = 20,
): Promise<ActivityPage> {
  const params = new URLSearchParams();
  if (cursor) params.set("cursor", cursor);
  params.set("limit", String(limit));
  const qs = params.toString();
  return api<ActivityPage>(`/v1/activity${qs ? `?${qs}` : ""}`);
}

export function fetchMe(): Promise<MeOut> {
  return api<MeOut>("/v1/me");
}

// ---------------------------------------------------------------------------
// Helpers — bridge wire strings to `bigint` for the Money component,
// which expects bigint minor units per §9 spec.
// ---------------------------------------------------------------------------

export function moneyMinor(m: MoneyWire): bigint {
  // Backend guarantees a base-10 integer string; BigInt throws on
  // anything else, which is the right behaviour (fail loud).
  return BigInt(m.amount);
}
